import {
	ColumnDef,
	ColumnFiltersState,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	Row,
	useReactTable,
	VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { HiOutlineDocumentArrowDown } from "react-icons/hi2";
import { cn } from "../../lib/utils";
import { TableHeaderRow } from "./TableHeaderRow";
import { TableRow } from "./TableRow";
import { fuzzyFilter, useTableColumnSizing, useTableSorting } from "./utils";

type RowDataWithSubItems<TData> = TData & {
	subItems?: RowDataWithSubItems<TData>[];
};

export type DataTableProps<TData, TValue> = {
	name: string;
	columnDefs: ColumnDef<RowDataWithSubItems<TData>, TValue>[];
	data?: RowDataWithSubItems<TData>[];
	loadingMessage?: string;
	columnVisibility?: VisibilityState;
	columnFilters?: ColumnFiltersState;
	getRowUrl?: (row: RowDataWithSubItems<TData>) => string;
	searchString?: string;
	expandToRows?: boolean;
	tableClassName?: string;
	tableHeaderClassName?: string;
	tableBodyClassName?: string;
	tableBodyBorderColor?: string;
	enableExportToCSV?: boolean;
	filterActions?: React.ReactNode;
};

export function DataTable<TData, TValue>({
	name,
	columnDefs,
	data,
	loadingMessage,
	columnVisibility,
	columnFilters,
	getRowUrl,
	searchString,
	expandToRows,
	tableClassName,
	tableHeaderClassName,
	tableBodyClassName,
	enableExportToCSV,
	filterActions,
}: DataTableProps<TData, TValue>) {
	const { columnSizing, handleColumnSizingChange } = useTableColumnSizing(
		`inrev_table_${name}`,
		columnDefs,
	);
	const { sorting, handleSortingChange } = useTableSorting(`${name}_sorting`);

	const [globalFilter, setGlobalFilter] = useState(searchString ?? "");
	const parentRef = useRef<HTMLDivElement>(null);

	const table = useReactTable<RowDataWithSubItems<TData>>({
		data: data ?? [],
		columns: columnDefs,
		state: {
			sorting,
			columnVisibility,
			columnFilters,
			globalFilter,
			columnSizing,
		},
		getSubRows: (row) => row.subItems,
		getRowCanExpand: (row) => row.subRows.length > 0,
		renderFallbackValue: "--",
		onGlobalFilterChange: setGlobalFilter,
		globalFilterFn: fuzzyFilter,
		onSortingChange: handleSortingChange,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		onColumnSizingChange: handleColumnSizingChange,
		autoResetAll: false,
		filterFromLeafRows: true, // So that parent rows are shown when a sub-row matches the filter
	});

	const { rows } = table.getRowModel();
	const { rows: filteredRows } = table.getFilteredRowModel();

	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 58,
		overscan: 20,
	});

	const { csvData, csvHeaders } = useMemo(
		() => ({
			csvData: filteredRows.map((row) => row.original) ?? [],
			csvHeaders: columnDefs.map((col) => {
				/* Handle special cases for known column IDs */
				if (col.id === "underwriter") {
					return {
						label: "Underwriter",
						key: "underwriterName",
					};
				}
				if (col.id === "agencyId") {
					return {
						label: "Agency",
						key: "agencyName",
					};
				}

				return {
					label: col.header as string,
					key: (col as any).accessorKey ?? col.id,
				};
			}),
		}),
		// Not ideal, but eliminates some unnecessary re-computes
		// Can't use table here because it never updates when filters are applied
		[JSON.stringify(filteredRows), columnDefs],
	);

	useEffect(() => {
		const newSearchString = searchString ?? "";
		if (globalFilter !== newSearchString) {
			setGlobalFilter(newSearchString);
		}
	}, [searchString]);

	return (
		// Parent container
		<div
			className={cn(
				"relative w-full",
				expandToRows ? "h-fit min-h-fit overflow-hidden" : "h-full",
				tableClassName,
			)}
			style={expandToRows ? { height: rowVirtualizer.getTotalSize() + 48 } : undefined}
		>
			{/* Actions */}
			{enableExportToCSV && (
				<div className="absolute top-[-40px] right-0">
					<div className="flex justify-end items-center pb-2">
						<span className="mr-auto">{filterActions}</span>
						<span title="Export to CSV" className="p-1 rounded-md hover:bg-gray-100 cursor-pointer">
							<CSVLink
								filename={`data-table-${name}-export.csv`}
								data={csvData}
								headers={csvHeaders}
							>
								<HiOutlineDocumentArrowDown size={20} />
							</CSVLink>
						</span>
					</div>
				</div>
			)}
			{/* Overflow wrapper */}
			<div className="overflow-hidden h-full w-full relative border border-gray-200 rounded-xl">
				<div
					ref={parentRef}
					className={cn("relative h-full w-full overflow-auto bg-gray-50 scrollbar-med")}
				>
					{/* Table */}
					<div className="w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
						{/* Header */}
						<TableHeaderRow
							headerGroups={table.getHeaderGroups()}
							setColumnSizing={handleColumnSizingChange}
							columnSizing={columnSizing}
							columnSizingInfo={table.getState().columnSizingInfo}
							className={tableHeaderClassName}
						/>
						{/* Body */}
						<div
							className={cn("min-w-full h-full relative", tableBodyClassName)}
							style={{ width: table.getTotalSize() }}
						>
							{rows.length > 0 &&
								rowVirtualizer.getVirtualItems().map((virtualRow) => {
									const row = rows[virtualRow.index] as Row<TData>;
									return (
										<TableRow
											key={row.id}
											height={virtualRow.size}
											translateY={virtualRow.start}
											row={row}
											getRowUrl={getRowUrl}
										/>
									);
								})}
							{!data && (
								<div className="mt-[32px] w-full text-center text-tertiary italic text-[13px] animate-pulse">
									{loadingMessage ?? "Loading..."}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
